<template>
    <header class="app-header" :class="{ 'app-header--scrolled': scrolled }">
        <div class="container app-header__inner">
            <Logo class="app-header__logo" />

            <nav class="app-header__nav" :aria-label="t('nav.menu')">
                <component
                    v-for="link in primaryLinks"
                    :key="link.key"
                    :is="link.to ? RouterLink : 'a'"
                    :to="link.to"
                    :href="link.href"
                    class="nav-link"
                    :class="{ 'nav-link--active': isActive(link) }"
                    @click="link.scrollTo ? scrollToSection(link.scrollTo, $event) : null"
                >
                    {{ link.label }}
                </component>
            </nav>

            <div class="app-header__actions">
                <LanguageSwitcher class="app-header__action app-header__action--desktop" />
                <ThemeSwitcher class="app-header__action app-header__action--desktop" />
                <button
                    type="button"
                    class="app-header__hamburger"
                    :aria-label="t('nav.menu')"
                    :aria-expanded="drawerOpen"
                    aria-controls="mobile-drawer"
                    @click="drawerOpen = true"
                >
                    <i class="dx-icon dx-icon-menu" aria-hidden="true"></i>
                </button>
            </div>
        </div>

        <Teleport to="body">
            <div
                class="mobile-overlay"
                :class="{ 'mobile-overlay--open': drawerOpen }"
                @click="drawerOpen = false"
            ></div>
            <aside
                id="mobile-drawer"
                class="mobile-drawer"
                :class="{ 'mobile-drawer--open': drawerOpen }"
                :aria-hidden="!drawerOpen"
                role="dialog"
                :aria-label="t('nav.menu')"
            >
                <div class="mobile-drawer__header">
                    <Logo />
                    <button
                        type="button"
                        class="mobile-drawer__close"
                        :aria-label="t('nav.menu')"
                        @click="drawerOpen = false"
                    >
                        <i class="dx-icon dx-icon-close" aria-hidden="true"></i>
                    </button>
                </div>
                <nav class="mobile-drawer__nav" :aria-label="t('nav.menu')">
                    <component
                        v-for="link in primaryLinks"
                        :key="`m-${link.key}`"
                        :is="link.to ? RouterLink : 'a'"
                        :to="link.to"
                        :href="link.href"
                        class="nav-link nav-link--mobile"
                        :class="{ 'nav-link--active': isActive(link) }"
                        @click="onMobileLinkClick(link, $event)"
                    >
                        {{ link.label }}
                    </component>
                </nav>
                <div class="mobile-drawer__footer">
                    <LanguageSwitcher />
                    <ThemeSwitcher />
                </div>
            </aside>
        </Teleport>
    </header>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import Logo from './Logo.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'
import ThemeSwitcher from './ThemeSwitcher.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const drawerOpen = ref(false)
const scrolled = ref(false)
const activeSection = ref('')

const primaryLinks = computed(() => [
    { key: 'home', to: '/', label: t('nav.home'), exact: true },
    { key: 'software', href: '/#software', scrollTo: 'software', label: t('nav.software'), section: 'software' },
    { key: 'hardware', to: '/hardware', label: t('nav.hardware') },
    { key: 'contact', href: '/#contact', scrollTo: 'contact', label: t('nav.contact'), section: 'contact' },
])

function isActive(link) {
    if (link.section) {
        return route.path === '/' && activeSection.value === link.section
    }
    if (link.to === '/' && link.exact) {
        return route.path === '/' && !activeSection.value
    }
    if (link.to) {
        return route.path === link.to
    }
    return false
}

async function scrollToSection(id, ev) {
    if (ev) ev.preventDefault()
    if (route.path !== '/') {
        await router.push({ path: '/', hash: `#${id}` })
        return
    }
    const el = document.getElementById(id)
    if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 70
        window.scrollTo({ top, behavior: 'smooth' })
        history.replaceState(null, '', `/#${id}`)
    }
}

async function onMobileLinkClick(link, ev) {
    drawerOpen.value = false
    if (link.scrollTo) {
        await scrollToSection(link.scrollTo, ev)
    }
}

function onScroll() {
    scrolled.value = window.scrollY > 8
    if (route.path !== '/') {
        activeSection.value = ''
        return
    }
    const sections = ['software', 'contact']
    const offset = 120
    let current = ''
    for (const id of sections) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top - offset <= 0 && rect.bottom - offset > 0) {
            current = id
        }
    }
    activeSection.value = current
}

watch(drawerOpen, (open) => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = open ? 'hidden' : ''
})

watch(
    () => route.fullPath,
    () => {
        drawerOpen.value = false
        onScroll()
    }
)

onMounted(() => {
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
    }
})
</script>

<style scoped>
.app-header {
    position: fixed;
    top: 0;
    inset-inline: 0;
    height: var(--gc-header-height);
    z-index: 1000;
    background-color: color-mix(in srgb, var(--gc-page-bg) 86%, transparent);
    backdrop-filter: saturate(160%) blur(12px);
    -webkit-backdrop-filter: saturate(160%) blur(12px);
    border-bottom: 1px solid transparent;
    transition: border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease;
}

.app-header--scrolled {
    border-bottom-color: var(--gc-border);
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.04);
}

.app-header__inner {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.app-header__logo {
    flex-shrink: 0;
}

.app-header__nav {
    display: none;
    align-items: center;
    gap: 0.25rem;
}

.app-header__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.app-header__action--desktop {
    display: none;
}

.nav-link {
    position: relative;
    padding: 0.5rem 0.85rem;
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--gc-text);
    border-radius: var(--gc-radius-sm);
    transition: color 160ms ease, background-color 160ms ease;
    cursor: pointer;
}

.nav-link:hover {
    color: var(--gc-accent);
    text-decoration: none;
    background-color: var(--gc-accent-soft);
}

.nav-link--active {
    color: var(--gc-accent);
}

.nav-link--active::after {
    content: '';
    position: absolute;
    inset-inline: 0.85rem;
    bottom: 4px;
    height: 2px;
    background-color: var(--gc-accent);
    border-radius: 2px;
}

.app-header__hamburger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    background: transparent;
    color: var(--gc-text);
    border-radius: var(--gc-radius-sm);
    cursor: pointer;
    font-size: 22px;
    transition: background-color 160ms ease;
}

.app-header__hamburger:hover {
    background-color: var(--gc-accent-soft);
    color: var(--gc-accent);
}

@media (min-width: 900px) {
    .app-header__nav {
        display: flex;
    }
    .app-header__action--desktop {
        display: inline-flex;
    }
    .app-header__hamburger {
        display: none;
    }
}
</style>

<style>
.mobile-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.45);
    opacity: 0;
    pointer-events: none;
    transition: opacity 220ms ease;
    z-index: 1099;
}

.mobile-overlay--open {
    opacity: 1;
    pointer-events: auto;
}

.mobile-drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(300px, 88vw);
    background-color: var(--gc-surface);
    border-left: 1px solid var(--gc-border);
    box-shadow: none;
    transform: translateX(100%);
    transition: transform 240ms ease;
    z-index: 1100;
    display: flex;
    flex-direction: column;
}

.mobile-drawer--open {
    transform: translateX(0);
}

.mobile-drawer__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.25rem;
    height: var(--gc-header-height);
    border-bottom: 1px solid var(--gc-border);
}

.mobile-drawer__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: 0;
    background: transparent;
    color: var(--gc-text);
    border-radius: var(--gc-radius-sm);
    cursor: pointer;
    font-size: 22px;
    transition: background-color 160ms ease;
}

.mobile-drawer__close:hover {
    background-color: var(--gc-accent-soft);
    color: var(--gc-accent);
}

.mobile-drawer__nav {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1rem 0.75rem;
    gap: 0.25rem;
    overflow-y: auto;
}

.mobile-drawer__nav .nav-link {
    position: relative;
    padding: 0.85rem 1rem;
    font-size: 1rem;
    font-weight: 600;
    border-radius: var(--gc-radius-md);
    color: var(--gc-text);
    text-decoration: none;
    transition: color 160ms ease, background-color 160ms ease;
}

.mobile-drawer__nav .nav-link:hover {
    color: var(--gc-accent);
    background-color: var(--gc-accent-soft);
}

.mobile-drawer__nav .nav-link--active {
    color: var(--gc-accent);
    background-color: var(--gc-accent-soft);
}

.mobile-drawer__footer {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-top: 1px solid var(--gc-border);
    flex-wrap: wrap;
}

@media (min-width: 900px) {
    .mobile-overlay,
    .mobile-drawer {
        display: none;
    }
}
</style>
